import { useEffect, useState } from "react"
import { Package, X } from "lucide-react"
import { StatusPill, formatDateTime } from "procurement/design"
import {
  useGetBookInventoryAllocationsQuery,
  useBookInventoryAllocationActionMutation,
} from "shared/redux/endpoints/procurementItems"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { Button, NumberControl } from "../../ui"
import { formatQty } from "../../bookInventory/model"

const ALLOCATION_PILL = {
  RESERVED: { tone: "info", label: "Reserved" },
  ISSUED: { tone: "success", label: "Issued" },
  RELEASED: { tone: "neutral", label: "Released" },
}

const num = (value) => Number(value) || 0

const ACTION_LABEL = { reserve: "Reserve", issue: "Issue", release: "Release" }
const ACTION_ORDER = ["issue", "release", "reserve"]

// The BE's allowedActions decides the buttons. Fallback for rows without it:
//   - issued (issuedQty > 0) → locked: the BE 409s every further action;
//   - books held (outstandingReservedQty > 0) → Issue / Release / Reserve more;
//   - otherwise → Reserve.
const rowActions = (entry) => {
  if (Array.isArray(entry.allowedActions)) {
    const allowed = entry.allowedActions.map((a) => String(a).toLowerCase())
    return ACTION_ORDER.filter((a) => allowed.includes(a))
  }
  if (num(entry.issuedQty) > 0) return []
  if (num(entry.outstandingReservedQty) > 0) return ACTION_ORDER
  return ["reserve"]
}

// Issue only succeeds once held = classItemNeededQty (the class's whole need
// for this item, across mapping types), so that's the figure to show.
const neededQty = (entry) => num(entry.classItemNeededQty ?? entry.quantityNeeded)

const isUnreserved = (entry) => num(entry.issuedQty) === 0 && num(entry.outstandingReservedQty) === 0 && neededQty(entry) > 0

const Stat = ({ label, value }) => (
  <div className="bi-stock-stat">
    <span>{label}</span>
    <strong>{formatQty(value)}</strong>
    <small>books</small>
  </div>
)

// View Stock for one center × book: stock totals + reserve → issue tracking
// per upcoming class. Actions confirm inline on the row (Issue can't be
// undone), then the query refetches from the server.
const ViewStockModal = ({ open, row, onClose }) => {
  const canLoad = open && row?.centerId != null && row?.itemId != null
  const { data, isFetching, isError, refetch } = useGetBookInventoryAllocationsQuery(
    { centerId: row?.centerId, itemId: row?.itemId },
    { skip: !canLoad }
  )
  const [runAction, { isLoading: acting }] = useBookInventoryAllocationActionMutation()
  // { key, classId, action, quantity } — the row currently asking for confirmation.
  // Keyed by row, not classId: a class mapped under 2 mapping types is 2 rows.
  const [pending, setPending] = useState(null)

  useEffect(() => setPending(null), [open, row?.centerId, row?.itemId])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  if (!open || !row) return null

  const classes = data?.classes || []
  const unreserved = classes.filter(isUnreserved).length

  // The BE rejects a reserve above either limit (409); checked here too so
  // the user sees why before submitting.
  const maxReserve = (entry) => {
    const remaining = neededQty(entry) - num(entry.issuedQty) - num(entry.outstandingReservedQty)
    const available = data?.availableToReserveQty
    return Math.max(0, available == null ? remaining : Math.min(remaining, num(available)))
  }

  const reserveError = (entry, quantity) => {
    const max = maxReserve(entry)
    if (max <= 0) return "Không thể reserve thêm: lớp đã đủ sách hoặc hết stock khả dụng."
    if (!(Number(quantity) > 0)) return "Nhập số lượng reserve lớn hơn 0."
    if (Number(quantity) > max) return `Tối đa ${formatQty(max)} cuốn (theo stock khả dụng và số lớp còn cần).`
    return null
  }

  const start = (key, entry, action) =>
    setPending({
      key,
      classId: entry.classId,
      action,
      quantity: action === "reserve" ? String(maxReserve(entry) || "") : "",
    })

  const confirm = async (entry) => {
    const { classId, action, quantity } = pending
    const error = action === "reserve" ? reserveError(entry, quantity) : null
    if (error) {
      showToastError(error)
      return
    }
    try {
      await runAction({
        action,
        classId,
        centerId: row.centerId,
        itemId: row.itemId,
        quantity: action === "reserve" ? quantity : undefined,
      }).unwrap()
      showToastSuccess(`${ACTION_LABEL[action]} thành công.`)
      setPending(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, `Không ${ACTION_LABEL[action].toLowerCase()} được. Vui lòng thử lại.`))
      if (e?.status === 409) refetch()
    }
  }

  return (
    <div className="ds-drawer-root">
      <div className="ds-drawer-backdrop bi-modal-backdrop" onClick={onClose} aria-hidden />
      <div className="bi-modal bi-modal--stock" role="dialog" aria-modal="true" aria-label="View Stock">
        <div className="bi-modal-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="pc-h1" style={{ fontSize: 18 }}>View Stock</h2>
            <div className="bi-muted" style={{ margin: 0 }}>
              {[row.centerName || `Center ${row.centerId}`, row.itemName].filter(Boolean).join(" · ")}
            </div>
          </div>
          <span className="bi-modal-icon" aria-hidden>
            <Package />
          </span>
          <button type="button" className="pc-btn pc-btn--ghost pc-btn--sm" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden />
          </button>
        </div>

        <div className="bi-modal-body">
          {isError ? (
            <p className="bi-note">
              Không tải được tồn kho.{" "}
              <button type="button" className="pc-btn pc-btn--ghost pc-btn--sm" onClick={refetch}>
                Thử lại
              </button>
            </p>
          ) : !data ? (
            <p className="bi-muted">Đang tải…</p>
          ) : (
            <>
              <div className="bi-stock-stats">
                <Stat label="Current Stock" value={data.currentStock} />
                <Stat label="Reserved chưa phát" value={data.outstandingReservedQty} />
                <Stat label="Total Issued" value={data.totalIssuedQty} />
                <Stat label="Có thể reserve" value={data.availableToReserveQty} />
                <Stat label="Có thể điều chuyển" value={data.transferableQty} />
              </div>
              <p className="bi-muted">
                {data.stockUpdatedAt
                  ? `Tồn kho cập nhật ${formatDateTime(data.stockUpdatedAt)}.`
                  : "Center chưa có bản ghi tồn kho cho sách này."}
                {" "}Số tổng tính trên mọi lớp của center × sách này.
              </p>

              <div className="bi-section-title" style={{ marginTop: 8 }}>
                Class Distribution Tracking
                {unreserved > 0 && <StatusPill tone="warning" label={`${unreserved} class chưa Reserve`} />}
              </div>

              {classes.length === 0 ? (
                <p className="bi-muted">Không có lớp nào cần sách này trong 30 ngày tới, và chưa có lớp nào giữ hay phát sách này.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="bi-class-table">
                    <thead>
                      <tr>
                        <th>Class</th>
                        <th className="is-num">Needed</th>
                        <th className="is-num">Reserved</th>
                        <th className="is-num">Issued</th>
                        <th>Status / Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classes.map((entry, idx) => {
                        const key = `${entry.classId}-${idx}`
                        const pill = ALLOCATION_PILL[entry.allocationStatus] ||
                          (isUnreserved(entry) ? { tone: "warning", label: "Chưa reserve" } : null)
                        const isPending = pending?.key === key
                        const qtyError = isPending && pending.action === "reserve" ? reserveError(entry, pending.quantity) : null
                        return (
                          <tr key={key}>
                            <td>
                              <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{entry.className}</div>
                              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                {[
                                  entry.status,
                                  entry.levelName,
                                  entry.rowScope === "ALLOCATION_HISTORY" ? "Ngoài 90 ngày" : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </div>
                            </td>
                            <td className="is-num">{formatQty(neededQty(entry))}</td>
                            <td className="is-num">{formatQty(entry.outstandingReservedQty)}</td>
                            <td className="is-num">{formatQty(entry.issuedQty)}</td>
                            <td>
                              {isPending ? (
                                <span className="bi-row-confirm">
                                  {pending.action === "reserve" ? (
                                    <NumberControl
                                      className="pc-control bi-qty-input"
                                      aria-label="Số lượng reserve"
                                      aria-invalid={!!qtyError}
                                      value={pending.quantity}
                                      onChange={(v) => setPending((p) => ({ ...p, quantity: v }))}
                                    />
                                  ) : (
                                    <span className="bi-muted" style={{ margin: 0 }}>
                                      {pending.action === "issue"
                                        ? `Phát ${formatQty(entry.outstandingReservedQty)} cuốn? Không sửa được sau khi phát.`
                                        : `Huỷ giữ ${formatQty(entry.outstandingReservedQty)} cuốn?`}
                                    </span>
                                  )}
                                  <Button size="sm" variant="primary" disabled={acting || !!qtyError} onClick={() => confirm(entry)}>
                                    {acting ? "…" : ACTION_LABEL[pending.action]}
                                  </Button>
                                  <Button size="sm" variant="ghost" disabled={acting} onClick={() => setPending(null)}>
                                    Huỷ
                                  </Button>
                                  {qtyError && (
                                    <span role="alert" style={{ flexBasis: "100%", fontSize: 12, color: "var(--pc-danger)" }}>
                                      {qtyError}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="bi-row-confirm">
                                  {pill && <StatusPill tone={pill.tone} label={pill.label} />}
                                  {rowActions(entry).map((action) => (
                                    <Button
                                      key={action}
                                      size="sm"
                                      variant={action === "issue" ? "primary" : "secondary"}
                                      disabled={acting || isFetching}
                                      onClick={() => start(key, entry, action)}
                                    >
                                      {ACTION_LABEL[action]}
                                    </Button>
                                  ))}
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bi-modal-foot" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <span className="bi-muted" style={{ margin: 0 }}>
            Issue giảm Stock đúng một lần; không cho sửa sau khi phát.
          </span>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  )
}

export default ViewStockModal
